import axios, {CancelToken} from "axios";
import {SurveyApi} from "../api/SurveyApi";
import {Utils} from "../utils";
import {ISurvey} from "../interfaces/ISurvey";
import {IUserResponseDto} from "../interfaces/IUserResponseDto";
import {ISaveResponseStatus} from "../interfaces/ISaveResponseStatus";
import {ILoginRequest} from "../interfaces/ILoginRequest";
import {ISurveyUserData} from "../interfaces/ISurveyUserData";


export function SurveyService() {

  const surveyApi = SurveyApi();

  /**
   * Method for getting user data
   * @param {string} request - request data
   * @param {CancelToken} ct - cancellation token
   * @return {Promise<ISurveyUserData | null>} user data. in case of errors returns null.
   */
  const getUserData = async (request: string, ct: CancelToken = axios.CancelToken.source().token): Promise<ISurveyUserData | null> => {
    try {
      const response = await surveyApi.getUserData(request, ct);
      return !Utils.isEmpty(response.data) ? response.data : null;
    } catch (error) {
      return null;
    }
  };

  /**
   * Method for login
   * @param {ILoginRequest} request - request data
   * @param {CancelToken} ct - cancellation token
   * @return {Promise<boolean>} true in case of successful login, false if not.
   */
  const login = async (request: ILoginRequest, ct: CancelToken = axios.CancelToken.source().token): Promise<boolean> => {
    try {
      const response = await surveyApi.login(request, ct);
      return response.status === 200;
    } catch (error) {
      return false;
    }
  };

  /**
   * Method for logout
   * @param {CancelToken} ct - cancellation token
   * @return {Promise<any | null>} response with status code
   */
  const logout = async (ct: CancelToken = axios.CancelToken.source().token): Promise<any | null> => {
    try {
      const response = await surveyApi.logout(ct);
      return !Utils.isEmpty(response.data) ? response.data : null;
    } catch (error) {
      return null;
    }
  };

  /**
   * Method for obtaining survey details
   * @param {string} id - survey id
   * @param {CancelToken} ct - cancellation token
   * @return {Promise<ISurvey>} survey details. in case of errors returns null.
   */
  const getSurvey = async (id: string, ct: CancelToken = axios.CancelToken.source().token): Promise<ISurvey | null> => {
    try {
      const response = await surveyApi.getSurvey(id, ct);
      return !Utils.isEmpty(response.data) ? response.data : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Method for submitting the survey
   * @param {IUserResponseDto} request - request data
   * @param {CancelToken} ct - cancellation token
   * @return {Promise<ISaveResponseStatus | null>} submitting status. in case of errors returns null
   */
  const postSurvey = async (request: IUserResponseDto, ct: CancelToken = axios.CancelToken.source().token): Promise<ISaveResponseStatus | null> => {
    try {
      const response = await surveyApi.postSurvey(request, ct);
      return !Utils.isEmpty(response.data) ? response.data : null;
    } catch (error) {
      return null;
    }
  };

  /**
   * Method for obtaining list of survey details
   * @param {CancelToken} ct - cancellation token
   * @return {Promise<ISurvey[]>} list of survey details. in case of errors returns empty array.
   */
  const getSurveys = async (ct: CancelToken = axios.CancelToken.source().token): Promise<ISurvey[]> => {
    try {
      const response = await surveyApi.getSurveys(ct);
      return !Utils.isEmpty(response.data) ? response.data : [];
    } catch (error) {
      return [];
    }
  }


  return {
    getUserData,
    login,
    logout,
    getSurvey,
    postSurvey,
    getSurveys
  };
}
